import { CobaltSidePanel } from '@cae/cobalt-react/components/side-panel';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import * as React from 'react';

import type { PastDueInvoicesPanelProps } from './PastDueInvoicesPanel.types';

import { PastDueInvoiceItem } from './past-due-invoice-item/PastDueInvoiceItem';
import styles from './PastDueInvoicesPanel.module.css';

const PastDueInvoicesPanel = React.memo(
  ({
    invoices,
    isOpen,
    isLoading,
    isError,
    onClose,
    onInvoiceClick,
  }: Readonly<PastDueInvoicesPanelProps>): React.ReactElement => {
    const isMobileOrTablet = useMediaQuery(MediaQuery.MobileAndTablet);

    const renderContent = React.useCallback((): React.ReactElement => {
      if (isLoading) {
        return <ContentLoader />;
      }

      if (isError) {
        return <InlineFeedback type="data-error" showGoHome={false} />;
      }

      return (
        <ul
          aria-label="Past due invoice alerts"
          className={styles.invoiceList}
          data-testid="past-due-invoices-panel-list"
        >
          {invoices.map(invoice => (
            <li
              key={invoice.accountReceivableId}
              className={styles.invoiceListItem}
            >
              <PastDueInvoiceItem
                invoice={invoice}
                onInvoiceClick={onInvoiceClick}
              />
            </li>
          ))}
        </ul>
      );
    }, [isLoading, isError, invoices, onInvoiceClick]);

    return (
      <div className={isMobileOrTablet ? undefined : styles.panelWrapper}>
        <CobaltSidePanel
          size={isMobileOrTablet ? 'large' : 'small'}
          isOpen={isOpen}
          mode={isMobileOrTablet ? 'over' : 'push'}
          showCloseButton
          onClose={onClose}
          title="Your alerts"
          description={
            !isLoading && !isError ? (
              <div className="body-02">
                You have{' '}
                <span className={styles.description}>
                  {invoices.length} {invoices.length === 1 ? 'alert' : 'alerts'}
                </span>{' '}
                for invoices past due.
              </div>
            ) : undefined
          }
          data-testid="past-due-invoices-panel"
          className={isMobileOrTablet ? undefined : styles.panelContainer}
        >
          {renderContent()}
        </CobaltSidePanel>
      </div>
    );
  }
);

PastDueInvoicesPanel.displayName = 'PastDueInvoicesPanel';

export { PastDueInvoicesPanel };
